import React, { Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useLocation } from 'react-router-dom';
import { Box, Skeleton, Stack } from '@mui/material';
import { CompetitionSelection } from '../app/CompetitionSelection';
import { FplConnectionGate } from '../modules/fantasy/components';
import { GameweekHubProvider } from '../modules/fantasy/context';
import { AppLayout } from '../layouts/AppLayout';
import { NotFound, ChampionsLeagueComingSoon } from '../shared/pages';

const PlayerResearchHub = React.lazy(() =>
  import('../modules/players').then((module) => ({ default: module.PlayerResearchHub }))
);
const PlayerExplorer = React.lazy(() =>
  import('../modules/players').then((module) => ({ default: module.PlayerExplorer }))
);
const PlayerComparePage = React.lazy(() =>
  import('../modules/players').then((module) => ({ default: module.PlayerComparePage }))
);
const PlayerDetailPage = React.lazy(() =>
  import('../modules/players').then((module) => ({ default: module.PlayerDetailPage }))
);
const ClubExplorer = React.lazy(() =>
  import('../modules/teams').then((module) => ({ default: module.ClubExplorer }))
);
const AseanCup2026TournamentCenter = React.lazy(() =>
  import('../modules/asean-cup-2026').then((module) => ({
    default: module.AseanCup2026TournamentCenter,
  }))
);
const FixturesAndResultsPage = React.lazy(() =>
  import('../modules/asean-cup-2026').then((module) => ({
    default: module.FixturesAndResultsPage,
  }))
);

const GameweekHubShell = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.GameweekHubShell }))
);
const FantasyGameOverview = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.FantasyGameOverview }))
);
const MyTeamPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.MyTeamPage }))
);
const GameweekCenterPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.GameweekCenterPage }))
);
const FixturesExplorerPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.FixturesExplorerPage }))
);
const LeagueActivityPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.LeagueActivityPage }))
);
const TransferPlannerPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.TransferPlannerPage }))
);
const GameweekPlannerPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.GameweekPlannerPage }))
);
const SeasonPlannerPage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.SeasonPlannerPage }))
);
const PremierLeagueTablePage = React.lazy(() =>
  import('../modules/fantasy/pages').then((module) => ({ default: module.PremierLeagueTablePage }))
);

const RouteLoadingFallback = () => (
  <Box sx={{ minHeight: '400px', p: 2 }}>
    <Stack spacing={1}>
      <Skeleton variant="text" width="28%" height={40} />
      <Skeleton variant="text" width="42%" />
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={120} />
      <Skeleton variant="rounded" height={120} />
    </Stack>
  </Box>
);

const LegacyFantasyRedirect: React.FC = () => {
  const location = useLocation();
  const nextPath = location.pathname.replace(
    '/premier-league/fantasy-game',
    '/premier-league/gameweek'
  );

  return <Navigate to={`${nextPath}${location.search}${location.hash}`} replace />;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <CompetitionSelection />,
  },
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        path: 'asean-cup-2026',
        children: [
          {
            index: true,
            element: (
              <Suspense fallback={<RouteLoadingFallback />}>
                <AseanCup2026TournamentCenter />
              </Suspense>
            ),
          },
          {
            path: 'fixtures',
            element: (
              <Suspense fallback={<RouteLoadingFallback />}>
                <FixturesAndResultsPage />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'premier-league',
        children: [
          {
            index: true,
            element: <Navigate to="home" replace />,
          },
          {
            path: 'home',
            element: (
              <GameweekHubProvider>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <FantasyGameOverview />
                </Suspense>
              </GameweekHubProvider>
            ),
          },
          {
            path: 'dashboard',
            element: <Navigate to="/premier-league/home" replace />,
          },
          {
            path: 'players',
            element: (
              <Suspense fallback={<RouteLoadingFallback />}>
                <PlayerResearchHub />
              </Suspense>
            ),
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <PlayerExplorer />
                  </Suspense>
                ),
              },
              {
                path: 'compare',
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <PlayerComparePage />
                  </Suspense>
                ),
              },
              {
                path: ':playerId',
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <PlayerDetailPage />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: 'analytics/*',
            element: <Navigate to="/premier-league/home" replace />,
          },
          {
            path: 'gameweek',
            element: (
              <GameweekHubProvider>
                <Suspense fallback={<RouteLoadingFallback />}>
                  <GameweekHubShell />
                </Suspense>
              </GameweekHubProvider>
            ),
            children: [
              {
                index: true,
                element: <Navigate to="my-team" replace />,
              },
              {
                path: 'overview',
                element: <Navigate to="/premier-league/home" replace />,
              },
              {
                path: 'connect',
                element: <Navigate to="/premier-league/home" replace />,
              },
              {
                path: 'my-team',
                element: (
                  <FplConnectionGate showConnectedSummary={false}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <MyTeamPage />
                    </Suspense>
                  </FplConnectionGate>
                ),
              },
              {
                path: 'league',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'league/:leagueId',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'league/:leagueId/live',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'league/:leagueId/managers/:managerId',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'activity',
                element: (
                  <FplConnectionGate showConnectedSummary={false}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <LeagueActivityPage />
                    </Suspense>
                  </FplConnectionGate>
                ),
              },
              {
                path: 'fixtures',
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <FixturesExplorerPage />
                  </Suspense>
                ),
              },
              {
                path: 'clubs',
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <ClubExplorer />
                  </Suspense>
                ),
              },
              {
                path: 'table',
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <PremierLeagueTablePage />
                  </Suspense>
                ),
              },
              {
                path: 'gameweeks',
                element: <Navigate to=".." replace />,
              },
              {
                path: 'team',
                element: <Navigate to="../my-team" replace />,
              },
              {
                path: 'gameweeks/:gameweekId',
                element: (
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <GameweekCenterPage />
                  </Suspense>
                ),
              },
              {
                path: 'transfers',
                element: (
                  <FplConnectionGate showConnectedSummary={false}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <TransferPlannerPage />
                    </Suspense>
                  </FplConnectionGate>
                ),
              },
              {
                path: 'planner',
                element: (
                  <FplConnectionGate showConnectedSummary={false}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <GameweekPlannerPage />
                    </Suspense>
                  </FplConnectionGate>
                ),
              },
              {
                path: 'season-planner',
                element: (
                  <FplConnectionGate showConnectedSummary={false}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <SeasonPlannerPage />
                    </Suspense>
                  </FplConnectionGate>
                ),
              },
              {
                path: 'transfer-planner',
                element: <Navigate to="../transfers" replace />,
              },
              {
                path: 'gameweek-planner',
                element: <Navigate to="../planner" replace />,
              },
              {
                path: 'leagues',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'leagues/:leagueId',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'leagues/:leagueId/live',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
              {
                path: 'leagues/:leagueId/managers/:managerId',
                element: <Navigate to="/premier-league/gameweek/my-team" replace />,
              },
            ],
          },
          {
            path: 'fixtures',
            element: <Navigate to="gameweek/fixtures" replace />,
          },
          {
            path: 'teams',
            element: <Navigate to="gameweek/clubs" replace />,
          },
          {
            path: 'fantasy-game/*',
            element: <LegacyFantasyRedirect />,
          },
        ],
      },
      {
        path: 'champions-league',
        children: [
          {
            index: true,
            element: <Navigate to="dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: <ChampionsLeagueComingSoon />,
          },
          {
            path: 'fixtures',
            element: <ChampionsLeagueComingSoon />,
          },
          {
            path: 'players',
            element: <ChampionsLeagueComingSoon />,
          },
          {
            path: 'analytics',
            element: <ChampionsLeagueComingSoon />,
          },
          {
            path: 'fantasy',
            element: <ChampionsLeagueComingSoon />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

export const Router: React.FC = () => <RouterProvider router={router} />;
